import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Eraser, Brush, Trash2, BookmarkPlus, Loader2, Play, Pause } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { api, errMsg, mediaUrl } from "@/lib/api";

const COLORS = ["#ff2d55", "#ffd60a", "#30d158", "#1e97f3"];
const tc = (n, fps) => { const f = Math.round(fps) || 24; const s = Math.floor(n / f); return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}:${String(n % f).padStart(2, "0")}`; };

export const Tools = ({ mode, setMode, color, setColor, size, setSize, onClear }) => (
  <div className="flex items-center gap-2 flex-wrap" data-testid="cp-doodle-tools">
    <button type="button" onClick={() => setMode("brush")} data-testid="cp-tool-brush" className={`ax-chip ${mode === "brush" ? "!border-[#1e97f3] text-white" : ""}`}><Brush className="w-3.5 h-3.5 mr-1" />Brush</button>
    <button type="button" onClick={() => setMode("erase")} data-testid="cp-tool-eraser" className={`ax-chip ${mode === "erase" ? "!border-[#1e97f3] text-white" : ""}`}><Eraser className="w-3.5 h-3.5 mr-1" />Eraser</button>
    {COLORS.map((c) => <button key={c} type="button" onClick={() => { setColor(c); setMode("brush"); }} data-testid={`cp-color-${c.slice(1)}`}
      className={`h-6 w-6 rounded-full border-2 ${color === c ? "border-white" : "border-transparent"}`} style={{ background: c }} />)}
    <div className="flex items-center gap-2 w-32"><span className="text-[10px] font-mono text-zinc-500">size</span>
      <Slider value={[size]} min={4} max={80} step={1} onValueChange={([v]) => setSize(v)} data-testid="cp-brush-size" className="flex-1" /></div>
    <button type="button" onClick={onClear} data-testid="cp-clear" className="ax-chip"><Trash2 className="w-3.5 h-3.5 mr-1" />Clear</button>
  </div>
);

export default function FrameEditor({ projectId, source, info, keyframes, onSave }) {
  const fps = info.fps || 24;
  const total = Math.max(1, info.frames || 1);
  const [n, setN] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [mode, setMode] = useState("brush");
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState(18);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const video = useRef(null);
  const canvas = useRef(null);
  const drawing = useRef(null);
  const existing = keyframes.find((k) => k.frame === n);

  const seek = useCallback((f) => {
    const v = Math.max(0, Math.min(total - 1, f));
    video.current?.pause();
    setN(v);
    if (video.current) video.current.currentTime = (v + 0.5) / fps;
  }, [fps, total]);

  useEffect(() => {
    const v = video.current;
    if (!v || !playing || !v.requestVideoFrameCallback) return undefined;
    let id;
    const cb = (_, meta) => { setN(Math.min(total - 1, Math.floor(meta.mediaTime * fps + 0.001))); id = v.requestVideoFrameCallback(cb); };
    id = v.requestVideoFrameCallback(cb);
    return () => v.cancelVideoFrameCallback?.(id);
  }, [playing, fps, total]);

  const toggle = useCallback(() => {
    const v = video.current;
    if (!v) return;
    if (v.paused) { if (n >= total - 1) v.currentTime = 0.5 / fps; v.play().catch(() => {}); } else { v.pause(); seek(Math.floor(v.currentTime * fps + 0.001)); }
  }, [n, total, fps, seek]);

  useEffect(() => {
    const onKey = (e) => {
      if (/INPUT|TEXTAREA|SELECT/.test(e.target.tagName) || e.target.isContentEditable || document.querySelector("[role=dialog]")) return;
      if (e.key === "ArrowRight") { e.preventDefault(); seek(n + (e.shiftKey ? 10 : 1)); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); seek(n - (e.shiftKey ? 10 : 1)); }
      else if (e.key === " ") { e.preventDefault(); toggle(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [n, seek, toggle]);

  useEffect(() => {
    const c = canvas.current;
    if (!c || playing) return;
    c.width = info.width || 1280; c.height = info.height || 720;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    if (existing?.doodle?.url) { const d = new Image(); d.crossOrigin = "anonymous"; d.onload = () => ctx.drawImage(d, 0, 0, c.width, c.height); d.src = mediaUrl(existing.doodle.url); }
    setDirty(false);
  }, [n, playing, existing?.doodle?.url, info.width, info.height]);

  const pos = (e) => { const c = canvas.current, r = c.getBoundingClientRect(); return [(e.clientX - r.left) * (c.width / r.width), (e.clientY - r.top) * (c.height / r.height), c.width / r.width]; };
  const down = (e) => { if (playing) toggle(); e.currentTarget.setPointerCapture(e.pointerId); drawing.current = pos(e); move(e); };
  const move = (e) => {
    if (!drawing.current) return;
    const [x, y, k] = pos(e), [px, py] = drawing.current;
    const ctx = canvas.current.getContext("2d");
    ctx.globalCompositeOperation = mode === "erase" ? "destination-out" : "source-over";
    ctx.strokeStyle = color; ctx.lineWidth = size * k; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.globalAlpha = 0.85;
    ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(x, y); ctx.stroke();
    drawing.current = [x, y, k];
    setDirty(true);
  };
  const up = () => { drawing.current = null; };
  const clear = () => { const c = canvas.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/bfx/cleanplate/keyframe", { project_id: projectId, reference_id: source.id, frame: n, doodle: canvas.current.toDataURL("image/png") }, { timeout: 120000 });
      onSave(data);
      setDirty(false);
    } catch (e) { toast.error(errMsg(e, "Could not save the keyframe")); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-[#1065c0]/40 bg-[#05080E]/70 p-4 mb-6" data-testid="cp-frame-editor">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="text-sm font-semibold text-white">Mark the areas to clean <span className="text-zinc-300 font-normal">— ← → step a frame · Shift ±10 · Space play</span></div>
        <Tools mode={mode} setMode={setMode} color={color} setColor={setColor} size={size} setSize={setSize} onClear={clear} />
      </div>
      <div className="relative mx-auto w-fit max-w-full bg-black rounded-lg overflow-hidden">
        <video ref={video} src={mediaUrl(info.proxy_url)} muted playsInline preload="auto" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
          onLoadedData={() => { if (video.current) video.current.currentTime = (n + 0.5) / fps; }} onEnded={() => seek(total - 1)}
          className="block max-h-[52vh] max-w-full" data-testid="cp-scrub-video" />
        <canvas ref={canvas} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
          className={`absolute inset-0 w-full h-full touch-none cursor-crosshair ${playing ? "opacity-0" : ""}`} data-testid="cp-doodle-canvas" />
        {existing && <div className="absolute bottom-2 left-2 ax-badge !text-[#64b7f6] pointer-events-none opacity-80" data-testid="cp-frame-is-keyframe">Keyframe</div>}
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button type="button" onClick={toggle} data-testid="cp-play-toggle" className="h-9 w-9 rounded-full bfx-gradient-bg text-white flex items-center justify-center shrink-0">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}</button>
        <button type="button" onClick={() => seek(n - 1)} data-testid="cp-prev-frame" className="ax-chip"><ChevronLeft className="w-4 h-4" /></button>
        <Slider value={[n]} min={0} max={total - 1} step={1} onValueChange={([v]) => seek(v)} data-testid="cp-frame-slider" className="flex-1" />
        <button type="button" onClick={() => seek(n + 1)} data-testid="cp-next-frame" className="ax-chip"><ChevronRight className="w-4 h-4" /></button>
        <span className="font-mono text-xs text-zinc-300 w-40 text-right" data-testid="cp-frame-number">frame {n} / {total - 1} · {tc(n, fps)}</span>
        <button type="button" onClick={save} disabled={playing || saving || (!dirty && !!existing)} data-testid="cp-save-keyframe"
          className="h-9 px-3 rounded-md text-xs font-semibold bfx-gradient-bg text-white disabled:opacity-40 flex items-center gap-1.5">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookmarkPlus className="w-4 h-4" />}{existing ? "Update Keyframe" : "Save Keyframe"}</button>
      </div>
      {!!keyframes.length && (
        <div className="flex gap-1.5 mt-3 flex-wrap" data-testid="cp-keyframe-jump">
          {keyframes.map((k) => <button key={k.frame} type="button" onClick={() => seek(k.frame)} className={`ax-chip text-[11px] ${k.frame === n ? "!border-[#1e97f3] text-white" : ""}`} data-testid={`cp-jump-${k.frame}`}>#{k.frame}</button>)}
        </div>
      )}
    </div>
  );
}
